import { all, call, getContext, put, race, select, takeEvery, throttle } from '@redux-saga/core/effects';
import { take } from 'redux-saga-test-plan/matchers';
import { takeLatest } from 'redux-saga/effects';
import type { Action } from '../utils';
import i18n from '../../../i18n';
import type {
    IDocument,
    IFetchDocumentsRequest,
    IFetchVaultsRequest,
    IMediaTypeRead,
    ISecureDocumentStoreSettings,
    ITag,
    IUpdateDocumentAttributesRequest,
    IUploadDocumentRequest,
    IUploadMultipleDocumentRequest,
    IVault,
} from '../../api/domain';
import type { SecureDocumentStoreApiService } from '../../api/services';
import { NotificationType } from '../../enum';
import { addNotification, hideProgressIndicator, showProgressIndicator } from '../application/application-actions';
import { logError, logTrace } from '../logging-api/logging-api-actions';
import { convertFilesToFormData } from '../utils';
import {
    deleteDocument,
    deleteDocumentFailure,
    deleteDocumentSuccess,
    deleteMultipleDocuments,
    deleteMultipleDocumentsFailure,
    deleteMultipleDocumentsSuccess,
    downloadDocument,
    downloadDocumentFailure,
    downloadDocumentSuccess,
    fetchAllDocuments,
    fetchAllDocumentsFailure,
    fetchAllDocumentsSuccess,
    fetchAllDocumentsThrottled,
    fetchDocument,
    fetchDocumentFailure,
    fetchDocumentsByIds,
    fetchDocumentsByIdsFailure,
    fetchDocumentsByIdsSuccess,
    fetchDocumentSuccess,
    fetchMediaTypes,
    fetchMediaTypesFailure,
    fetchMediaTypesSuccess,
    fetchSettings,
    fetchSettingsFailure,
    fetchSettingsSuccess,
    fetchTags,
    fetchTagsFailure,
    fetchTagsSuccess,
    fetchTotalAmountOfDocumentsSuccess,
    fetchUnreadDocumentCount,
    fetchUnreadDocumentCountFailure,
    fetchUnreadDocumentCountSuccess,
    fetchVaults,
    fetchVaultsFailure,
    fetchVaultsSuccess,
    markDocumentAsRead,
    markDocumentAsReadFailure,
    markDocumentAsReadSuccess,
    markDocumentAsUnread,
    markDocumentAsUnreadFailure,
    markDocumentAsUnreadSuccess,
    updateDocument,
    updateDocumentFailure,
    updateDocumentSuccess,
    uploadDocument,
    uploadDocumentFailure,
    uploadDocuments,
    uploadDocumentsFailure,
    uploadDocumentsSuccess,
    uploadDocumentSuccess,
    uploadSimpleFiles,
    uploadSimpleFilesFailure,
    uploadSimpleFilesSuccess,
} from './secure-document-store-api-actions';
import { getDocuments, getVaults } from './secure-document-store-api-selectors';
import de from './translations/de.json';
import en from './translations/en.json';
import fr from './translations/fr.json';
import it from './translations/it.json';

function* addTranslations() {
    i18n.addResources('de', de);
    i18n.addResources('en', en);
    i18n.addResources('fr', fr);
    i18n.addResources('it', it);

    yield;
}

function* handleFetchDocuments(action: Action<{ request: IFetchDocumentsRequest; hideProgressIndicator?: boolean }>) {
    const api: SecureDocumentStoreApiService = yield getContext('secureDocumentStoreApiService');
    try {
        if (!action.payload.hideProgressIndicator) {
            yield put(showProgressIndicator());
        }
        const { documents, numberOfDocuments } = yield call([api, api.fetchDocuments], action.payload.request);
        yield put(fetchAllDocumentsSuccess(documents));
        yield put(fetchTotalAmountOfDocumentsSuccess(numberOfDocuments));
    } catch (error: unknown) {
        yield put(logError(`could not fetch documents: ${error}`));
        yield put(fetchAllDocumentsFailure());
    } finally {
        if (!action.payload.hideProgressIndicator) {
            yield put(hideProgressIndicator());
        }
    }
}

function* handleFetchDocumentsByIds({ payload: documentIds }: Action<string[]>) {
    const api: SecureDocumentStoreApiService = yield getContext('secureDocumentStoreApiService');
    try {
        const documents: IDocument[] = yield all(documentIds.map((documentId) => call([api, api.fetchDocument], documentId)));
        yield put(fetchDocumentsByIdsSuccess(documents));
    } catch (error: unknown) {
        yield put(logError(`could not fetch documents by id: ${error}`));
        yield put(fetchDocumentsByIdsFailure());
    }
}

/**
 *  Hideous implementation due to missing possibility to fetch unread documents. Watch out for CSDEV-2724
 */
function* handleFetchUnreadDocumentCount() {
    const api: SecureDocumentStoreApiService = yield getContext('secureDocumentStoreApiService');
    try {
        const vaults: IVault[] = yield select(getVaults);
        const unreadDocumentsCounts: number[] = yield all(vaults.map(({ commonObject: { id } }) => call([api, api.fetchDocumentUnreadCount], id)));
        yield put(fetchUnreadDocumentCountSuccess(unreadDocumentsCounts.reduce((acc, val) => acc + val, 0)));
    } catch (error: unknown) {
        yield put(logError(`could not fetch documents: ${error}`));
        yield put(fetchUnreadDocumentCountFailure());
    }
}

function* handleFetchDocument(action: Action<string>) {
    const api: SecureDocumentStoreApiService = yield getContext('secureDocumentStoreApiService');
    const { payload } = action;
    try {
        yield put(showProgressIndicator());
        const document: IDocument = yield call([api, api.fetchDocument], payload);
        yield put(fetchDocumentSuccess(document));
    } catch (error: unknown) {
        yield put(logError(`could not fetch document with id ${payload}: ${error}`));
        yield put(fetchDocumentFailure());
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleDeleteDocument(action: Action<{ documentId: string; request: IFetchDocumentsRequest }>) {
    const api: SecureDocumentStoreApiService = yield getContext('secureDocumentStoreApiService');
    const { documentId, request } = action.payload;
    try {
        yield put(showProgressIndicator());
        yield call([api, api.deleteDocument], { id: documentId });
        yield put(deleteDocumentSuccess());
        yield put(fetchAllDocuments({ request, hideProgressIndicator: false }));
    } catch (error: unknown) {
        yield put(logError(`could not delete document with id ${documentId}: ${error}`));
        yield put(deleteDocumentFailure());
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleDeleteDocumentSuccess() {
    yield put(fetchUnreadDocumentCount());
    yield put(
        addNotification({
            title: i18n.t('secure-document-store-api.success.delete-files'),
            type: NotificationType.SUCCESS,
        }),
    );
}

function* handleDeleteDocumentFailure() {
    yield put(
        addNotification({
            title: i18n.t('secure-document-store-api.failure.delete-files'),
            type: NotificationType.ERROR,
        }),
    );
}

function* handleDeleteMultipleDocuments(action: Action<{ documentIds: string[]; request: IFetchDocumentsRequest }>) {
    const api: SecureDocumentStoreApiService = yield getContext('secureDocumentStoreApiService');
    const { documentIds, request } = action.payload;
    try {
        yield put(showProgressIndicator());
        yield all(documentIds.map((id) => call([api, api.deleteDocument], { id })));
        yield put(deleteMultipleDocumentsSuccess());
        yield put(fetchAllDocuments({ request, hideProgressIndicator: false }));
    } catch (error: unknown) {
        yield put(logError(`could not delete documents [${documentIds.join(',')}] : ${error}`));
        yield put(deleteMultipleDocumentsFailure());
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleUpdateDocument(action: Action<{ request: IUpdateDocumentAttributesRequest; showNotification: boolean }>) {
    const api: SecureDocumentStoreApiService = yield getContext('secureDocumentStoreApiService');
    const { payload } = action;
    try {
        yield put(showProgressIndicator());
        yield call([api, api.updateDocument], payload.request);
        yield put(fetchDocument(payload.request.id));
        yield put(updateDocumentSuccess());
        if (payload.showNotification) {
            yield put(
                addNotification({
                    title: i18n.t('secure-document-store-api.success.update-file'),
                    type: NotificationType.SUCCESS,
                }),
            );
        }
    } catch (error: unknown) {
        yield put(logError(`could not update document with id ${payload.request.id}: ${error}`));
        yield put(updateDocumentFailure());
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleUpdateDocumentFailure() {
    yield put(
        addNotification({
            title: i18n.t('secure-document-store-api.error.update-file'),
            type: NotificationType.ERROR,
        }),
    );
}

function* handleFetchSettings() {
    const api: SecureDocumentStoreApiService = yield getContext('secureDocumentStoreApiService');
    try {
        const settings: ISecureDocumentStoreSettings = yield call([api, api.fetchSettings]);
        yield put(fetchSettingsSuccess(settings));
    } catch (error: unknown) {
        yield put(logError(`could not fetch settings: ${error}`));
        yield put(fetchSettingsFailure());
    }
}

function* handleFetchTags() {
    const api: SecureDocumentStoreApiService = yield getContext('secureDocumentStoreApiService');
    try {
        const tags: ITag[] = yield call([api, api.fetchTags]);
        yield put(fetchTagsSuccess(tags));
    } catch (error: unknown) {
        yield put(logError(`could not fetch tags: ${error}`));
        yield put(fetchTagsFailure());
    }
}

function* handleFetchMediaTypes() {
    const api: SecureDocumentStoreApiService = yield getContext('secureDocumentStoreApiService');
    try {
        const mediaTypes: IMediaTypeRead[] = yield call([api, api.fetchMediaTypes]);
        yield put(fetchMediaTypesSuccess(mediaTypes));
    } catch (error: unknown) {
        yield put(logError(`could not fetch mediaTypes: ${error}`));
        yield put(fetchMediaTypesFailure());
    }
}

function* handleFetchVaults(action: Action<IFetchVaultsRequest>) {
    const api: SecureDocumentStoreApiService = yield getContext('secureDocumentStoreApiService');
    try {
        const vaults: IVault[] = yield call([api, api.fetchVaults], action.payload);
        yield put(fetchVaultsSuccess(vaults));
    } catch (error: unknown) {
        yield put(logError(`could not fetch vaults: ${error}`));
        yield put(fetchVaultsFailure());
    }
}

function* handleUploadSimpleFiles(action: Action<{ files: File[]; vaultId: string; request: IFetchDocumentsRequest }>) {
    const { files, vaultId, request } = action.payload;

    try {
        yield put(showProgressIndicator());
        const uploadDocumentRequest: IUploadMultipleDocumentRequest = {
            vaultId,
            formData: convertFilesToFormData(files),
        };
        yield put(uploadDocuments(uploadDocumentRequest));
        const { uploadDocumentsSuccessAction, uploadDocumentsFailureAction } = yield race({
            uploadDocumentsSuccessAction: take(uploadDocumentsSuccess),
            uploadDocumentsFailureAction: take(uploadDocumentsFailure),
        });
        if (uploadDocumentsFailureAction) {
            throw new Error('Some uploads failed');
        }
        const documentIds: string[] = uploadDocumentsSuccessAction.payload;
        if (!documentIds || !documentIds.length) {
            throw new Error('The upload request succeeded but returned no documents');
        }
        yield put(uploadSimpleFilesSuccess(documentIds));
        yield put(fetchAllDocuments({ request, hideProgressIndicator: false }));
    } catch (error: unknown) {
        yield put(logError(`could not upload files: ${error}`));
        yield put(uploadSimpleFilesFailure());
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleUploadSimpleFilesSuccess() {
    yield put(
        addNotification({
            title: i18n.t('secure-document-store-api.success.upload-files'),
            type: NotificationType.SUCCESS,
        }),
    );
}

function* handleUploadSimpleFilesFailure() {
    yield put(
        addNotification({
            title: i18n.t('secure-document-store-api.error.upload-files'),
            type: NotificationType.ERROR,
        }),
    );
}

function* handleUploadDocuments(action: Action<IUploadMultipleDocumentRequest>) {
    const documentIds: string[] = [];
    const {
        payload: { formData, vaultId },
    } = action;
    try {
        for (const data of formData) {
            yield put(
                uploadDocument({
                    vaultId,
                    formData: data,
                }),
            );

            const { success, failure } = yield race({
                success: take(uploadDocumentSuccess),
                failure: take(uploadDocumentFailure),
            });

            if (failure) {
                throw new Error(`Could not upload single document to ${vaultId}`);
            }

            documentIds.push(success.payload);
        }
        yield put(uploadDocumentsSuccess(documentIds));
    } catch (error: unknown) {
        yield put(logError(`could not upload documents: ${error}`));
        yield put(uploadDocumentsFailure());
    }
}

function* handleUploadDocument(action: Action<IUploadDocumentRequest>) {
    const api: SecureDocumentStoreApiService = yield getContext('secureDocumentStoreApiService');
    try {
        const documentId: string = yield call([api, api.uploadDocument], action.payload.vaultId, action.payload.formData);
        yield put(markDocumentAsRead(documentId));
        yield take([markDocumentAsReadSuccess, markDocumentAsReadFailure]);

        yield put(uploadDocumentSuccess(documentId));
    } catch (error: unknown) {
        yield put(logError(`could not upload document: ${error}`));
        yield put(uploadDocumentFailure());
    }
}

function* handleDownloadDocument(action: Action<string>) {
    const api: SecureDocumentStoreApiService = yield getContext('secureDocumentStoreApiService');
    yield put(logTrace(`downloading document with id ${action.payload}`));
    try {
        const documents: IDocument[] = yield select(getDocuments);
        const doc: IDocument =
            documents.find((document) => document.commonObject.id === action.payload) || (yield call([api, api.fetchDocument], action.payload));

        if (!doc) {
            throw new Error(`could not find document with id ${action.payload}`);
        }

        const link = document.createElement('a');
        link.href = `${doc.publicUrl}`;
        link.download = doc.name;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        yield put(downloadDocumentSuccess());
    } catch (error: unknown) {
        yield put(logError(`could not download document with id ${action.payload}: ${error}`));
        yield put(downloadDocumentFailure());
    }
}

function* handleMarkDocumentAsRead(action: Action<string>) {
    const api: SecureDocumentStoreApiService = yield getContext('secureDocumentStoreApiService');
    try {
        yield call([api, api.markDocumentAsRead], action.payload);
        yield put(markDocumentAsReadSuccess());
        yield put(fetchUnreadDocumentCount());
    } catch (error: unknown) {
        yield put(markDocumentAsReadFailure());
    }
}

function* handleMarkDocumentAsUnread(action: Action<string>) {
    const api: SecureDocumentStoreApiService = yield getContext('secureDocumentStoreApiService');

    try {
        yield call([api, api.markDocumentAsUnread], action.payload);
        yield put(markDocumentAsUnreadSuccess());
        yield put(fetchUnreadDocumentCount());
    } catch (error: unknown) {
        yield put(markDocumentAsUnreadFailure());
    }
}

export function* secureDocumentStoreApiSaga() {
    yield all([
        call(addTranslations),
        takeEvery(downloadDocument, handleDownloadDocument),
        takeEvery(uploadDocument, handleUploadDocument),
        takeEvery(uploadDocuments, handleUploadDocuments),
        takeEvery(uploadSimpleFiles, handleUploadSimpleFiles),
        takeEvery(uploadSimpleFilesSuccess, handleUploadSimpleFilesSuccess),
        takeEvery(uploadSimpleFilesFailure, handleUploadSimpleFilesFailure),
        takeEvery(fetchUnreadDocumentCount, handleFetchUnreadDocumentCount),
        takeEvery(fetchDocument, handleFetchDocument),
        takeEvery(fetchSettings, handleFetchSettings),
        takeEvery(fetchTags, handleFetchTags),
        takeEvery(fetchMediaTypes, handleFetchMediaTypes),
        takeEvery(fetchAllDocuments, handleFetchDocuments),
        takeLatest(fetchDocumentsByIds, handleFetchDocumentsByIds),
        throttle(500, fetchAllDocumentsThrottled, handleFetchDocuments),
        takeEvery(fetchVaults, handleFetchVaults),
        takeEvery(updateDocument, handleUpdateDocument),
        takeEvery(updateDocumentFailure, handleUpdateDocumentFailure),
        takeEvery(deleteDocument, handleDeleteDocument),
        takeEvery(deleteDocumentSuccess, handleDeleteDocumentSuccess),
        takeEvery(deleteDocumentFailure, handleDeleteDocumentFailure),
        takeEvery(deleteMultipleDocuments, handleDeleteMultipleDocuments),
        takeEvery(deleteMultipleDocumentsSuccess, handleDeleteDocumentSuccess),
        takeEvery(deleteMultipleDocumentsFailure, handleDeleteDocumentFailure),
        takeEvery(markDocumentAsRead, handleMarkDocumentAsRead),
        takeEvery(markDocumentAsUnread, handleMarkDocumentAsUnread),
    ]);
}
